import { Component, computed, ElementRef, inject, input, output, Renderer2, viewChild } from '@angular/core';

import { select, Store } from '@ngxs/store';
import {
  AddAllVehiclesOnPageToSelections,
  ConfigurationState,
  InventoryManagementAuctionItemStatus,
  InventoryManagementDealerFilter, InventoryManagementOfferActions, InventoryManagementSellerOfferStatus,
  InventoryManagementState,
  LaunchDarklyService,
  RemoveAllVehiclesOnPageFromSelections,
  StatusFilter,
  ToggleStateService,
  VehicleToggleState
} from 'remarketing-angular-library';
import { AnalyticsService, SaComponent } from 'smartauction-frontend-micro-library';
import { TranslocoDirective } from '@jsverse/transloco';
import { DateRangeComponent } from '../date-range/date-range.component';
import { TrapFocusComponent } from 'remarketing-ui-components-lib';
import { MobileSearchFilterComponent } from '../search-filter/mobile-search-filter/mobile-search-filter.component';
import { CommonModule } from '@angular/common';
import { SearchResultsSortSelectComponentV2 } from '../search-results-sort-select/search-results-sort-select.component';
import { SearchFilterSectionService } from '../../services/search-filter-section/search-filter-section.service';
import { FilterSectionTypes } from '../../enums/inventory-management.enum';
import { OfferFilterService } from '../../services/offer-filter/offer-filter.service';

@Component({
  selector: 'app-utility-bar',
  templateUrl: './utility-bar.component.html',
  styleUrls: ['./utility-bar.component.scss'],
  imports: [
    TranslocoDirective,
    SearchResultsSortSelectComponentV2,
    TrapFocusComponent,
    MobileSearchFilterComponent,
    CommonModule,
    DateRangeComponent
  ]
})
export class UtilityBarComponent extends SaComponent {

  isExporting = input<boolean>(false);
  isSellReportingFeatureEnabled = input<boolean>();
  inServiceVehiclesEnabled = input<boolean>();
  singleListSelected = input<boolean>();
  disableSelectAll = input<boolean>();
  isFilterByInServiceVehicles = input<boolean>(false);

  exported = output<void>();
  exportedAll = output<void>();
  mobileFilter = output<boolean>();

  exportButton = viewChild<ElementRef>('exportButton');

  selectAllChecked$ = select(VehicleToggleState.selectAllChecked);
  inventoryManagementCurrentStatus$ = select(InventoryManagementState.currentStatus);
  protected inventoryManagementSearchCriteria$ = select(InventoryManagementState.searchCriteria);
  protected isListView$ = select(InventoryManagementState.isListViewToggled);
  protected configuration$ = select(ConfigurationState.configuration);

  private _mobileFiltersVisible = false;
  protected dateRangeError = false;
  private store: Store;
  private renderer: Renderer2;
  readonly toggleState: ToggleStateService;
  readonly launchDarklyService: LaunchDarklyService;
  readonly analyticsService: AnalyticsService;
  private readonly searchFilterSectionService: SearchFilterSectionService;
  private readonly offerFilterService: OfferFilterService;

  protected isOffersByDealerFilterSelectedAndActive$ = computed(() => {
    return (
      (this.searchFilterSectionService.getOpenFilterSection() === FilterSectionTypes.OFFERS ||
        this.searchFilterSectionService.getOpenFilterSection() === undefined) &&
      this.inventoryManagementSearchCriteria$().filterByDealer === InventoryManagementDealerFilter.OFFERS_BY_DEALER
    );
  });

  protected tenantName$ = computed(() => {
    return this.configuration$()?.tenantName;
  });

  get mobileFiltersVisible() {
    return this._mobileFiltersVisible;
  }

  set mobileFiltersVisible(val: boolean) {
    if (val) {
      this.renderer.addClass(window.document.body, 'search-results--mobile-filters-open');
    } else {
      this.renderer.removeClass(window.document.body, 'search-results--mobile-filters-open');
    }
    this._mobileFiltersVisible = val;
  }

  get noMargin(): boolean {
    return !this.soldStatusFilterSelected;
  }

  get soldStatusFilterSelected() {
    return this.inventoryManagementCurrentStatus$() === StatusFilter.SOLD_VEHICLES;
  }

  get isSelectAllEnabled() {
    return (
      ((this.isSellReportingFeatureEnabled() && this.soldStatusFilterSelected) ||
        (this.isOfferListSellerSelectAllEnabled && this.singleListSelected()) ||
        (this.inServiceVehiclesEnabled() &&
          this.isFilterByInServiceVehicles() &&
          this.inventoryManagementCurrentStatus$() === StatusFilter.ACTIVE_VEHICLES) ||
        this.inventoryManagementCurrentStatus$() === StatusFilter.RESERVED_VEHICLES) &&
      !this.disableSelectAll()
    );
  }

  get isAdminSelectAllEnabled() {
    return (
      this.launchDarklyService.getFeatureFlag('sell-adminSelectAllEnabled', false) &&
      this.inventoryManagementCurrentStatus$() !== StatusFilter.REMOVED_VEHICLES
    );
  }

  get manageInventoryMaxSelections(): number {
    return this.launchDarklyService.getFeatureFlag('sell-manageInventoryMaxSelections', -1);
  }

  get disableCheckbox(): boolean {
    return (
      !this.selectAllChecked$() &&
      this.manageInventoryMaxSelections > -1 &&
      this.store.selectSnapshot(VehicleToggleState.selectedVehicles)?.length >= this.manageInventoryMaxSelections
    );
  }

  toggleSelectAll() {
    let vehicles = this.store.selectSnapshot(InventoryManagementState.searchResponse)?.data;

    if (this.isOfferListSellerSelectAllEnabled && this.inventoryManagementCurrentStatus$() === StatusFilter.SHARED_VEHICLES) {
      const validBuyerActions = new Set<string>([
        InventoryManagementSellerOfferStatus.BUYER_CREATED,
        InventoryManagementSellerOfferStatus.BUYER_OFFERED,
        InventoryManagementSellerOfferStatus.BUYER_COUNTERED
      ]);

      vehicles = vehicles.filter(vehicle => {
        const latestOffer = this.offerFilterService.filterLatestOfferByOfferList(vehicle);
        return vehicle.auctionItemStatus === InventoryManagementAuctionItemStatus.ACTIVE && vehicle.offers
          && latestOffer && (validBuyerActions.has(latestOffer.status));
      });
    }

    // the search response should never be undefined, adding fail safe just in case
    if (!vehicles?.length) {
      return;
    }

    this.analyticsService.trackEvent('checkbox', 'Selected all vehicles - ' + !this.selectAllChecked$());
    if (!this.selectAllChecked$()) {
      this.store.dispatch(new AddAllVehiclesOnPageToSelections(vehicles));
    } else {
      this.store.dispatch(new RemoveAllVehiclesOnPageFromSelections(vehicles));
    }
  }

    get isExportResultsEnabled() {
    return this.launchDarklyService.getFeatureFlag('sell-exportResultsEnabled', false);
  }

    get isExportAllEnabled() {
      return this.launchDarklyService.getFeatureFlag('search-sellExportAllSoldEnabled', false) && this.soldStatusFilterSelected;
    }

  constructor() {
    super();
    this.store = inject(Store);
    this.renderer = inject(Renderer2);
    this.toggleState = inject(ToggleStateService);
    this.launchDarklyService = inject(LaunchDarklyService);
    this.analyticsService = inject(AnalyticsService);
    this.searchFilterSectionService = inject(SearchFilterSectionService);
    this.offerFilterService = inject(OfferFilterService);
  }

    export() {
    this.exported.emit();
  }

  exportAll() {
    this.exportedAll.emit();
  }

  toggleMobileFilter() {
    this.mobileFiltersVisible = !this.mobileFiltersVisible;
    this.mobileFilter.emit(this.mobileFiltersVisible);
    if (this.mobileFiltersVisible) {
      document.querySelector('#manage-header').setAttribute('aria-hidden', 'true');
      document.querySelector('#search-form').setAttribute('aria-hidden', 'true');
      document.querySelector('#active-vehicles-count').setAttribute('aria-hidden', 'true');
      document.querySelector('#utility-bar').setAttribute('aria-hidden', 'true');
      document.querySelector('#layout-body').setAttribute('aria-hidden', 'true');
    } else {
      document.querySelector('#manage-header').setAttribute('aria-hidden', 'false');
      document.querySelector('#search-form').setAttribute('aria-hidden', 'false');
      document.querySelector('#active-vehicles-count').setAttribute('aria-hidden', 'false');
      document.querySelector('#utility-bar').setAttribute('aria-hidden', 'false');
      document.querySelector('#layout-body').setAttribute('aria-hidden', 'false');
    }
  }

  handleDateRangeError(hasError: boolean) {
    this.dateRangeError = hasError;
  }

  get isOfferListSellerSelectAllEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('offerlist-seller-selectAllEnabled', true);
  }
}
